/**
 * Operator activity tracking - the "am I still being used?" signal.
 *
 * WHY THIS EXISTS
 *   Session renewal in `SessionGuard` runs on a timer and knew nothing about
 *   the operator. That is wrong in both directions at once:
 *
 *     - a tab left open and untouched renewed itself indefinitely, staying
 *       signed in for as long as the refresh token lived (~7 days), and
 *     - the wording of the expiry dialog ("the session has timed out") implied
 *       an idle policy that was not actually implemented anywhere.
 *
 *   The policy this file implements is the one operators expect: while you are
 *   working, the session slides forward silently and you are never interrupted;
 *   it ends only after a full hour in which you did nothing.
 *
 * ACTIVITY IS SHARED ACROSS TABS, DELIBERATELY
 *   The timestamp lives in `localStorage`, not in a module variable, so working
 *   in one CMS tab keeps every other tab alive too. A per-tab timer would expire
 *   the tab you left on a second monitor while you were busy in the first, and
 *   then - because `clearSession` wipes storage for everybody - take the tab you
 *   were actually using down with it.
 *
 * WHAT COUNTS AS ACTIVITY
 *   Real input only: pointer, keyboard, wheel, touch, scroll, and bringing the
 *   tab back to the foreground. Deliberately NOT background XHR - the CMS polls,
 *   and letting a poll count as activity would make the idle limit unreachable
 *   and this whole file decorative.
 *
 * WRITE THROTTLING
 *   `scroll` and `pointermove`-class events fire in bursts. Writes are throttled
 *   to one every few seconds, which is far finer than an hour-long limit needs
 *   and keeps a synchronous `localStorage` write off the scroll path.
 */

/** How long the operator may do nothing before the session ends. */
export const IDLE_LIMIT_MS = 60 * 60 * 1000;

/** Shared across tabs on purpose - see the note above. */
export const ACTIVITY_KEY = "lastActivityAt";

const WRITE_THROTTLE_MS = 5 * 1000;

/**
 * `capture: true` matters: the CMS scrolls inner containers, and a scroll inside
 * a div does not bubble to `window`. Capture sees it on the way down.
 * `passive: true` keeps these handlers off the critical path of the gesture.
 */
const ACTIVITY_EVENTS = [
  "pointerdown",
  "keydown",
  "wheel",
  "touchstart",
  "scroll",
];

const LISTENER_OPTIONS = { passive: true, capture: true };

let lastWrite = 0;

/**
 * Note that the operator is present.
 *
 * @param {boolean} [force] bypass the throttle. Used when a session begins, so
 *        the clock starts from a known point rather than from whatever the
 *        previous operator left behind.
 */
export const recordActivity = (force = false) => {
  const now = Date.now();
  if (!force && now - lastWrite < WRITE_THROTTLE_MS) return;

  lastWrite = now;
  try {
    localStorage.setItem(ACTIVITY_KEY, String(now));
  } catch (_error) {
    // Private-mode or quota failure. Losing the timestamp means the idle limit
    // cannot be enforced in this tab; that is a far better outcome than
    // throwing from a scroll handler.
  }
};

/** The stored timestamp, or `null` when absent or unusable. */
export const getLastActivityTime = () => {
  const raw = Number(localStorage.getItem(ACTIVITY_KEY));
  return Number.isFinite(raw) && raw > 0 ? raw : null;
};

/**
 * Milliseconds of grace left; negative once the idle limit has passed.
 * `null` when no timestamp is stored, which is treated as "not idle" - see
 * `isIdleExpired`.
 */
export const msUntilIdleExpiry = () => {
  const last = getLastActivityTime();
  return last === null ? null : last + IDLE_LIMIT_MS - Date.now();
};

/**
 * True once the operator has been away past the limit.
 *
 * A MISSING TIMESTAMP IS NOT IDLE, and that is a required safety property, not
 * laziness: `clearSession` removes the key, so an operator signing in again
 * after being timed out starts with no timestamp. Reading that as "idle" would
 * expire the brand-new session on the guard's very next tick and leave them
 * stuck in a sign-in loop they cannot escape.
 */
export const isIdleExpired = () => {
  const remaining = msUntilIdleExpiry();
  return remaining !== null && remaining <= 0;
};

/**
 * Start watching for input. Seeds the timestamp immediately so there is always
 * a baseline - without it, a session that is never touched would have no idle
 * clock at all and could not expire.
 *
 * @returns {() => void} removes every listener
 */
export const installActivityTracking = () => {
  const onActivity = () => recordActivity();

  const onVisibility = () => {
    // Returning to the tab is itself activity. Forced past the throttle because
    // this is a single, meaningful event, not a burst.
    if (document.visibilityState === "visible") recordActivity(true);
  };

  recordActivity(true);

  ACTIVITY_EVENTS.forEach((name) =>
    window.addEventListener(name, onActivity, LISTENER_OPTIONS),
  );
  document.addEventListener("visibilitychange", onVisibility);

  return () => {
    ACTIVITY_EVENTS.forEach((name) =>
      window.removeEventListener(name, onActivity, LISTENER_OPTIONS),
    );
    document.removeEventListener("visibilitychange", onVisibility);
  };
};
